import { Link, Outlet, useRouterState } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useI18n } from "../i18n/I18nProvider";
import type { MessageKey } from "../i18n/en";
import { Badge } from "../components/ui/feedback";
import { TabNav, type TabNavItem } from "../components/ui/TabNav";
import { adminInviteRequestsQuery } from "./queries";

const TABS = [
  { to: "/admin", labelKey: "admin.tabs.workspaces" },
  { to: "/admin/invites", labelKey: "admin.tabs.invites" },
  { to: "/admin/operators", labelKey: "admin.tabs.operators" },
  { to: "/admin/audit", labelKey: "admin.tabs.audit" },
  { to: "/admin/errors", labelKey: "admin.tabs.errors" },
] as const satisfies readonly { to: string; labelKey: MessageKey }[];

type AdminTo = (typeof TABS)[number]["to"];

/** Admin shell: section tabs (the shared TabNav) above the routed admin pages. */
export function AdminLayout() {
  const { t } = useI18n();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const requests = useQuery(adminInviteRequestsQuery("new"));
  const newRequests = Array.isArray(requests.data) ? requests.data.length : 0;
  const items: TabNavItem[] = TABS.map((tab) => ({
    key: tab.to,
    label:
      tab.to === "/admin/invites" && newRequests > 0 ? (
        <>
          {t(tab.labelKey)} <Badge tone="accent">{newRequests}</Badge>
        </>
      ) : (
        t(tab.labelKey)
      ),
    href: tab.to,
    active: tab.to === "/admin" ? pathname === "/admin" : pathname.startsWith(tab.to),
  }));
  const tabByKey = new Map<string, AdminTo>(TABS.map((tab) => [tab.to, tab.to]));
  return (
    <div className="space-y-6">
      <TabNav
        ariaLabel={t("admin.nav.label")}
        items={items}
        renderLink={(item, className) => {
          const to = tabByKey.get(item.key);
          return to ? (
            <Link to={to} className={className}>
              {item.label}
            </Link>
          ) : null;
        }}
      />
      <Outlet />
    </div>
  );
}
